import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert } from '@mui/material';
import { GridRowParams } from '@mui/x-data-grid-pro';
import { GridInitialStatePro } from '@mui/x-data-grid-pro/models/gridStatePro';
import { PageLayout } from '../../PageLayout';
import { PageCard } from '../../Compounds/CardWithTitle';
import { Text } from '../../Atoms/Text';
import { UpstackDataGrid } from '../../Compounds/UpstackDataGrid/UpstackDataGrid';
import { getGridColumns } from '../../Compounds/UpstackDataGrid/helpers';
import { AdviseSubNav } from '../../Compounds/Advise/AdviseSubNav';
import { NewEngagementButton } from '../../Compounds/Advise/NewEngagementButton';
import { NewEngagementDialog } from '../../Compounds/Advise/NewEngagementDialog/NewEngagementDialog';
import {
  GridPreferences,
  PrefKey,
  getPreferencesByKey,
  getUserPreferencesSubject,
  getUser,
  gridInitialState,
  isAdviseTeamManager
} from '../../../store/User';
import { addError } from '../../../store/Error';
import { fetchMyEngagements, fetchTeamEngagements } from '../../../Api/Advise';
import { EngagementListItem } from '../../../types/advise';
import { EngagementGridColumnDefs, mergeEngagements, toEngagementGridRow } from './EngagementGridColumnDefs';
import { EngagementActionNeededBox } from './EngagementActionNeededBox';
import { useEngagementActionNeeded } from './useEngagementActionNeeded';

const GRID_KEY: PrefKey = 'advise_dashboard_grid';

const defaultGridSettings = (): GridPreferences => ({
  muiConfig: gridInitialState() as GridInitialStatePro,
  disabledFields: []
});

export const AdviseDashboardPage = () => {
  const navigate = useNavigate();
  const [engagements, setEngagements] = useState<EngagementListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [teamLoadFailed, setTeamLoadFailed] = useState(false);
  const [gridSettings, setGridSettings] = useState<GridPreferences>(defaultGridSettings);
  const [newEngagementOpen, setNewEngagementOpen] = useState(false);
  const actionNeeded = useEngagementActionNeeded();
  const mounted = useRef(true);

  const loadEngagements = useCallback(async () => {
    const [own, team] = await Promise.all([
      fetchMyEngagements(),
      isAdviseTeamManager(getUser()) ? fetchTeamEngagements() : null
    ]);
    if (!mounted.current) return;
    addError('advise-engagement', (own.error ?? team?.error)?.message);
    setLoadFailed(!!own.error);
    setTeamLoadFailed(!!team?.error);
    setEngagements(mergeEngagements(own.data ?? [], team?.data ?? []));
    setLoading(false);
  }, []);

  useEffect(() => {
    mounted.current = true;
    loadEngagements();

    const settingsSub = getUserPreferencesSubject().subscribe(() => {
      const saved = getPreferencesByKey(GRID_KEY) as GridPreferences | undefined;
      if (saved) setGridSettings(saved);
    });

    return () => {
      mounted.current = false;
      settingsSub.unsubscribe();
    };
  }, [loadEngagements]);

  const handleConfigureLater = () => {
    setNewEngagementOpen(false);
    loadEngagements();
    actionNeeded.refresh();
  };

  const rows = useMemo(() => engagements.map((engagement) => toEngagementGridRow(engagement)), [engagements]);
  const columns = useMemo(() => getGridColumns(rows, EngagementGridColumnDefs, GRID_KEY), [rows]);
  const showEmptyState = !loading && !loadFailed && !teamLoadFailed && engagements.length === 0;

  return (
    <PageLayout
      pageTitle="Advise Dashboard"
      pageActions={<NewEngagementButton onClick={() => setNewEngagementOpen(true)} />}
      dataCy="advise-dashboard-page">
      <AdviseSubNav />
      <EngagementActionNeededBox
        report={actionNeeded.report}
        pendingIds={actionNeeded.pendingIds}
        onSnooze={actionNeeded.snooze}
        onClear={actionNeeded.clear}
      />
      <PageCard
        title="Engagements"
        dataCy="advise-dashboard-engagements"
        childrenWrapperClass="min-h-80 min-w-full">
        {loadFailed && (
          <Alert
            severity="error"
            className="mb-4">
            Your engagements could not be loaded. Refresh the page to try again.
          </Alert>
        )}
        {teamLoadFailed && !loadFailed && (
          <Alert
            severity="warning"
            className="mb-4">
            Your team&apos;s engagements could not be loaded. Refresh the page to try again.
          </Alert>
        )}
        {showEmptyState ? (
          <div
            className="flex flex-col items-center justify-center py-16 gap-3"
            data-testid="advise-dashboard-empty-state">
            <Text
              size="lg"
              weight="medium"
              color="grey8">
              No engagements yet
            </Text>
            <Text
              size="sm14"
              color="grey6"
              className="text-center max-w-md">
              Advise gives you and your customer one structured, data-backed place to evaluate options — from discovery
              through supplier scoring, pricing and ROI to a recommendation your customer can act on. Start an
              engagement to begin.
            </Text>
            <NewEngagementButton onClick={() => setNewEngagementOpen(true)} />
          </div>
        ) : (
          <UpstackDataGrid
            dataCy="advise-dashboard-grid"
            title="Engagements"
            rows={rows}
            columns={columns}
            loadingData={loading}
            page={GRID_KEY}
            gridSettings={gridSettings}
            showSearch={true}
            onRowClick={(params: GridRowParams) => navigate(`/advise/analysis?id=${params.id}`)}
          />
        )}
      </PageCard>
      <NewEngagementDialog
        open={newEngagementOpen}
        onCancel={() => setNewEngagementOpen(false)}
        onConfigureLater={handleConfigureLater}
      />
    </PageLayout>
  );
};
